import axios from "axios";
import type { AxiosInstance } from "axios";

export interface CoinPair {
  symbol: string;
  baseCoin: string;
  quoteCoin: string;
  status: string;
  minOrderQty: string;
  minOrderAmt: string;
  basePrecision: string;
  quotePrecision: string;
  tickSize: string;
}

export interface Coin {
  code: string;
  name: string;
  icon: string;
  description: string;
  category: string;
  pairs: CoinPair[];
}

export interface CoinsResponse {
  success: boolean;
  data: Coin[];
}

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    config.withCredentials = true;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      console.error("[marketApi] Erro na resposta:", {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
      });
    }
    return Promise.reject(error);
  },
);

export async function getCoins(category?: string): Promise<Coin[]> {
  try {
    const params = category ? { category } : undefined;
    const response = await axiosInstance.get("/market/coins", {
      params,
    });
    const body = response.data;
    // Support both { success, data: Coin[] } and plain Coin[] responses
    const coins = Array.isArray(body) ? body : body?.data;
    if (!Array.isArray(coins)) {
      throw new Error("Resposta inesperada da API /market/coins");
    }
    return coins as Coin[];
  } catch (error: any) {
    if (error.response) {
      const status = error.response.status;
      const statusText = error.response.statusText;
      const errorMessage = error.response.data?.message ||
        error.response.data || statusText;
      throw new Error(
        `Erro ao buscar coins: ${status} ${statusText} - ${errorMessage}`,
      );
    } else if (error.request) {
      throw new Error(
        "Erro de rede ao buscar coins. Verifique sua conexão.",
      );
    } else {
      throw new Error(`Erro ao buscar coins: ${error.message}`);
    }
  }
}
